import { FC } from 'react';
import { Divider, Stack } from '@chakra-ui/react';

import useDebounce from 'common/hooks/useDebounce';
import * as Form from 'components/_core/form';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineModal from 'components/_core/SunshineModal';
import Loader from 'components/Loader';
import { formatAddress } from 'helpers/address';
import { capitalize } from 'helpers/string';

import {
  ExternalCompany,
  useCompanyLookupQuery,
} from './hooks/useCompanyLookupQuery';
import locales from './locales';

interface ExernalCompaniesPickerProps {
  mapExternalCompanyWithClient: (company: ExternalCompany) => void;
  searchInput: string;
  setIsSearchingCompanies: (b: boolean) => void;
  setSearchInput: (s: string) => void;
}

const ExernalCompaniesPicker: FC<ExernalCompaniesPickerProps> = ({
  mapExternalCompanyWithClient,
  searchInput,
  setIsSearchingCompanies,
  setSearchInput,
}) => {
  const debouncedSearchInput = useDebounce<string>(searchInput, 500);

  const { companies, loading: companiesLoading } =
    useCompanyLookupQuery(debouncedSearchInput);

  return (
    <SunshineModal.Body flexBasis="100vh">
      <Stack spacing="space-24">
        <Form.Field label={locales.searchInputLabel}>
          <Form.Input
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            defaultValue={searchInput}
            name="search"
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={locales.searchInputPlaceholder}
          />
        </Form.Field>

        <Divider />

        {companiesLoading ? (
          <Loader />
        ) : (
          <SunshineCard.Group
            {...(companies.length === 0 && {
              border: 'none',
            })}
          >
            {companies.map((company) => (
              <SunshineCard
                key={company.legalRegistrationNumber}
                onClick={() => {
                  mapExternalCompanyWithClient(company);
                  setIsSearchingCompanies(false);
                }}
              >
                <SunshineCard.Content>
                  <SunshineCard.Title>
                    {capitalize(company?.legalName ?? '')}
                  </SunshineCard.Title>

                  <SunshineCard.Text>
                    {capitalize(
                      formatAddress({
                        city: company.city,
                        street: company.street,
                        zip: company.zip,
                      }),
                    )}
                  </SunshineCard.Text>
                </SunshineCard.Content>

                <SunshineCard.Slot name="action">
                  <SunshineCard.SunshineIcon name="chevron-right" />
                </SunshineCard.Slot>
              </SunshineCard>
            ))}
          </SunshineCard.Group>
        )}
      </Stack>
    </SunshineModal.Body>
  );
};

export default ExernalCompaniesPicker;
